/**
 * ProfileSetupWizard — iter v28 r419 (User M1328+M1329)
 *
 * Wizard "completa profilo" che si apre SOLO al primo login di un account
 * davvero nuovo (account < 7 giorni, niente annunci, niente contatti).
 *
 * Flusso a 5 step:
 *   1. Nome agenzia + Telefono  (obbligatori — no skip)
 *   2. Logo agenzia + Foto agente (opzionali con "Salta")
 *   3. Email professionale + Indirizzo + Sito web (opzionali)
 *   4. Headline pagina stima personale (opzionale)
 *   5. Estensione Chrome (link + "Fatto")
 *
 * Alla fine emette `pronto:start-tour-after-wizard` per innescare
 * `OnboardingMagico` (tour 5 slide che spiega l'app).
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle, Sparkle, Building, Phone, X as XIcon,
  Lightning, GoogleChromeLogo, ArrowSquareOut, UploadSimple, EnvelopeSimple,
  Globe, MapPin, User as UserIcon, Image as ImageIcon,
} from "@phosphor-icons/react";
import { CHROME_STORE_URL } from "@/lib/extensionConstants";

const DISMISS_KEY = "pronto:profile-wizard-dismissed";
const TOTAL_STEPS = 5;

// iter v28 r419 — Skip wizard se l'utente è già operativo.
//   accountState = { hasProperties: bool, hasContacts: bool }
// Account nuovo = creato meno di 7 giorni fa, senza annunci né contatti.
// iter v32 r25 — Pre-skip per account demo/review (Apple Reviewer, etc).
export function shouldShowProfileWizard(user, accountState = {}) {
  if (!user) return false;
  // 0. Account demo/review → mai mostrare wizard.
  const demoEmails = new Set([
    "apple-review@prontoimmo.it",
    "review@prontoimmo.it",
    "demo@prontoimmo.it",
  ]);
  if (user.email && demoEmails.has(user.email.toLowerCase())) return false;
  // 1. Già operativo? L'agente ha già usato l'app → non rompere.
  if (accountState.hasProperties || accountState.hasContacts) return false;
  // 2. Account vecchio? Niente wizard.
  if (user.created_at) {
    try {
      const ageDays = (new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24);
      if (ageDays > 7) return false;
    } catch { /* parse error, ignore */ }
  }
  // 3. Profilo già completo (i 2 minimi)?
  if (user.agency_name && user.agency_phone) {
    // Profilo minimo ok. Se ha anche logo + foto → niente nudge.
    if (user.agency_logo_url && user.agent_photo_url) return false;
    // Altrimenti il wizard è opzionale: lo mostriamo se non già dismissed.
  }
  // 4. Già dismissed?
  try {
    return !localStorage.getItem(DISMISS_KEY);
  } catch {
    return true;
  }
}

export default function ProfileSetupWizard({ user, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  // Step 1 — base
  const [agencyName, setAgencyName] = useState(user?.agency_name || "");
  const [agencyPhone, setAgencyPhone] = useState(user?.agency_phone || "");
  // Step 2 — visivo
  const [logoUrl, setLogoUrl] = useState(user?.agency_logo_url || "");
  const [photoUrl, setPhotoUrl] = useState(user?.agent_photo_url || "");
  // Step 3 — contatti pubblici
  const [agencyEmail, setAgencyEmail] = useState(user?.agency_email || "");
  const [agencyAddress, setAgencyAddress] = useState(user?.agency_address || "");
  const [agencyWebsite, setAgencyWebsite] = useState(user?.agency_website || "");
  // Step 4 — voice
  const [stimaHeadline, setStimaHeadline] = useState(user?.stima_headline || "");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setAgencyName(user?.agency_name || "");
    setAgencyPhone(user?.agency_phone || "");
    setLogoUrl(user?.agency_logo_url || "");
    setPhotoUrl(user?.agent_photo_url || "");
    setAgencyEmail(user?.agency_email || "");
    setAgencyAddress(user?.agency_address || "");
    setAgencyWebsite(user?.agency_website || "");
    setStimaHeadline(user?.stima_headline || "");
  }, [user]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    onClose?.();
  };

  // iter v30 r147 — Fix bug tastiera bloccata su iOS: tap fuori dagli input
  // chiude la tastiera (blur dell'elemento attivo). Necessario perché su
  // iOS Capacitor con contentInset:'never' la tastiera può coprire i bottoni
  // di azione e l'utente resta intrappolato senza modo di chiuderla.
  const dismissKeyboard = (e) => {
    const tag = e.target.tagName;
    if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "BUTTON" && tag !== "SELECT" && tag !== "A") {
      try { document.activeElement?.blur(); } catch { /* ignore */ }
    }
  };

  // Step 1 → Step 2 · salva nome + telefono prima di proseguire
  const submitStep1 = async () => {
    if (!agencyName.trim() || !agencyPhone.trim()) return;
    setBusy(true);
    try {
      await api.put("/profile/agency", {
        agency_name: agencyName.trim(),
        agency_phone: agencyPhone.trim(),
      });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  // Step 3 → Step 4 · salva email + indirizzo + sito (tutti opzionali)
  const submitStep3 = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (agencyEmail.trim()) payload.agency_email = agencyEmail.trim();
      if (agencyAddress.trim()) payload.agency_address = agencyAddress.trim();
      if (agencyWebsite.trim()) payload.agency_website = agencyWebsite.trim();
      if (Object.keys(payload).length > 0) {
        await api.put("/profile/agency", payload);
      }
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  // Step 4 → Step 5 · salva headline (opzionale)
  const submitStep4 = async () => {
    setBusy(true);
    try {
      if (stimaHeadline.trim()) {
        await api.put("/profile/agency", { stima_headline: stimaHeadline.trim() });
      }
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  // Fine · setta flag + emette evento per OnboardingMagico
  const finish = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    // iter v28 r421 — Persisto un flag in localStorage perché Home fa
    // `window.location.reload()` in onComplete (per ricaricare i dati profilo
    // aggiornati). Dopo il reload OnboardingMagico legge il flag e mostra
    // il banner "Bravo · profilo completato" sulla slide 0.
    try { localStorage.setItem("pronto:came-from-wizard", "1"); } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent("pronto:start-tour-after-wizard"));
    } catch { /* ignore */ }
    toast.success("Profilo pronto · ora ti spiego l'app");
    onComplete?.();
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      style={{ paddingTop: "max(1rem, calc(env(safe-area-inset-top) + 8px))" }}
      data-testid="profile-wizard"
      onClick={dismissKeyboard}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
      >
        <div className="px-5 sm:px-6 py-4 border-b border-[#EAE7DF] flex items-start gap-3 shrink-0">
          <div className="w-10 h-10 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shrink-0">
            <Sparkle size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E]">Benvenuto · 4 minuti</div>
            <h3 className="font-editorial text-[20px] sm:text-[22px] text-[#1C1C1A] leading-tight">Completa il profilo</h3>
          </div>
          {/* iter v30 r147 — Bottone chiudi ingrandito (44x44 hit area iOS HIG)
              per essere sempre tappabile anche con tastiera aperta. */}
          <button
            onClick={dismiss}
            className="w-11 h-11 -mr-2 -mt-1 flex items-center justify-center text-[#9A968B] hover:text-[#1C1C1A] active:bg-[#F4F1EA] rounded-full transition-colors"
            aria-label="Chiudi"
            data-testid="profile-wizard-close"
          >
            <XIcon size={22} weight="bold" />
          </button>
        </div>

        {/* Progress bar — 5 step */}
        <div className="flex items-center gap-1.5 px-5 sm:px-6 pt-4 shrink-0">
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n} className={`flex-1 h-1 rounded-full transition-colors ${step >= n ? "bg-[#7C8B6E]" : "bg-[#E5E7E0]"}`} />
          ))}
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-4 overflow-y-auto flex-1">
          {step === 1 && (
            <Step1Base
              userName={user?.name || ""}
              agencyName={agencyName} setAgencyName={setAgencyName}
              agencyPhone={agencyPhone} setAgencyPhone={setAgencyPhone}
              onNext={submitStep1} busy={busy}
            />
          )}
          {step === 2 && (
            <Step2Visual
              logoUrl={logoUrl} setLogoUrl={setLogoUrl}
              photoUrl={photoUrl} setPhotoUrl={setPhotoUrl}
              onBack={() => setStep(1)} onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <Step3Contacts
              agencyEmail={agencyEmail} setAgencyEmail={setAgencyEmail}
              agencyAddress={agencyAddress} setAgencyAddress={setAgencyAddress}
              agencyWebsite={agencyWebsite} setAgencyWebsite={setAgencyWebsite}
              onBack={() => setStep(2)} onNext={submitStep3} busy={busy}
            />
          )}
          {step === 4 && (
            <Step4Headline
              value={stimaHeadline} setValue={setStimaHeadline}
              onBack={() => setStep(3)} onNext={submitStep4} busy={busy}
            />
          )}
          {step === 5 && (
            <Step5Extension onBack={() => setStep(4)} onFinish={finish} />
          )}
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-[#EAE7DF] flex items-center justify-between gap-2 bg-[#FAFAF7] shrink-0">
          <span className="text-[11px] text-[#9A968B] uppercase tracking-[0.15em] font-bold">
            Step {step} / {TOTAL_STEPS}
          </span>
          <button
            onClick={() => { dismiss(); navigate("/dashboard/profile"); }}
            className="text-[12px] text-[#7C8B6E] hover:text-[#1C1C1A] font-semibold inline-flex items-center gap-1"
            data-testid="wizard-goto-profile"
          >
            Profilo completo →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Step 1 · Base (raccomandati) ─────────── */
function Step1Base({ userName, agencyName, setAgencyName, agencyPhone, setAgencyPhone, onNext, busy }) {
  const canNext = agencyName.trim() && agencyPhone.trim();

  // iter v29 r201 — Anti-duplicazione nome. Se l'agente è "Luca Ferrari" e
  // scrive "Casa Bella di Luca Ferrari" nel nome agenzia, nei contenuti
  // social (volantino, brochure) "Luca Ferrari" apparirebbe due volte.
  // Avvisiamo l'utente in modo soft (non blocchiamo, è una scelta sua).
  const nameLooksDuplicated = (() => {
    if (!userName || !agencyName) return false;
    const u = userName.trim().toLowerCase();
    const a = agencyName.trim().toLowerCase();
    if (u.length < 4 || a.length < 4) return false;
    // Match se il nome è contenuto come parola intera nell'agenzia
    return new RegExp(`\\b${u.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}\\b`).test(a);
  })();

  return (
    <div data-testid="wizard-step-1">
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A6A4D] inline-flex items-center gap-1.5">
        <Building size={12} weight="fill" /> Step 1 · I dati essenziali
      </div>
      <h4 className="text-[16px] font-semibold text-[#1C1C1A] mt-1.5">
        Come si chiama la tua agenzia o il tuo brand?
      </h4>
      <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
        Solo il <strong>nome del brand</strong> con cui ti presenti.
        Il tuo nome personale {userName ? <>(<strong>{userName}</strong>) </> : ""}
        è già salvato e comparirà accanto al brand nei contenuti.
      </p>
      <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-bold mt-4 block">
        Nome agenzia / brand
      </label>
      <input
        type="text"
        value={agencyName}
        onChange={(e) => setAgencyName(e.target.value)}
        placeholder="es. Tecnocasa Milano · Casa Bella · Studio Immobiliare"
        className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
        data-testid="wizard-agency-name"
        autoFocus
      />
      {nameLooksDuplicated && (
        <p
          className="text-[11.5px] text-[#A36B17] mt-1.5 leading-snug bg-[#FFF7E6] border border-[#FCE3B0] rounded-md px-2.5 py-1.5"
          data-testid="wizard-agency-name-duplicate-warn"
        >
          Hai inserito anche il tuo nome qui dentro. Nei volantini e nei post
          comparirebbe due volte. Lascia solo il nome del brand, ci pensiamo noi
          ad aggiungere &laquo;{userName}&raquo; dove serve.
        </p>
      )}
      <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-bold mt-3 block">Telefono / WhatsApp Business</label>
      <input
        type="tel"
        value={agencyPhone}
        onChange={(e) => setAgencyPhone(e.target.value)}
        placeholder="es. +39 333 1234567"
        className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
        data-testid="wizard-agency-phone"
      />
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext || busy}
        className="w-full mt-4 bg-[#1C1C1A] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#7C8B6E] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="wizard-next-1"
      >
        {busy ? "Salvo…" : <>Avanti <ArrowRight size={14} weight="bold" /></>}
      </button>
      {/* iter v33 r106 · Skip anche allo step 1 — nessun campo deve
          impedire al nuovo utente di iniziare a provare Pronto. */}
      <button
        type="button"
        onClick={() => {
          try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
          window.dispatchEvent(new CustomEvent("pronto:profile-wizard-dismissed"));
          window.location.reload();
        }}
        className="w-full mt-2 text-[13px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium py-2 transition-colors"
        data-testid="wizard-skip-1"
      >
        Lo completo più tardi
      </button>
    </div>
  );
}

/* ─────────── Step 2 · Logo + Foto agente ─────────── */
function Step2Visual({ logoUrl, setLogoUrl, photoUrl, setPhotoUrl, onBack, onNext }) {
  const logoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const onLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/agency/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data?.agency_logo_url || data?.url || "");
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo non caricato");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/agent/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(data?.agent_photo_url || data?.url || "");
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Foto non caricata");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="wizard-step-2">
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A6A4D] inline-flex items-center gap-1.5">
        <ImageIcon size={12} weight="fill" /> Step 2 · Visivo
      </div>
      <h4 className="text-[16px] font-semibold text-[#1C1C1A] mt-1.5">La tua faccia + il tuo logo</h4>
      <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
        Le persone seguono persone, non loghi. Mettici la tua faccia e il logo dell&apos;agenzia: appaiono sui volantini, sulla pagina stima e nei messaggi WhatsApp.
      </p>

      <div className="grid grid-cols-2 gap-3 mt-4">
        {/* Foto agente */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#6B6A65] mb-1.5">Foto agente</div>
          <div className="relative bg-[#FAFAF7] border border-[#E5E7E0] rounded-lg overflow-hidden aspect-square">
            {photoUrl ? (
              <img src={photoUrl} alt="Foto agente" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#C4C0B5]">
                <UserIcon size={36} weight="duotone" />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="w-full mt-2 px-3 py-2 border border-[#1C1C1A] text-[#1C1C1A] rounded-lg text-[12px] font-semibold hover:bg-[#1C1C1A] hover:text-white transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="wizard-upload-photo"
          >
            <UploadSimple size={13} weight="bold" /> {uploadingPhoto ? "…" : photoUrl ? "Cambia" : "Carica"}
          </button>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoSelected} />
        </div>

        {/* Logo agenzia */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#6B6A65] mb-1.5">Logo agenzia</div>
          <div className="relative bg-[#FAFAF7] border border-[#E5E7E0] rounded-lg overflow-hidden aspect-square flex items-center justify-center p-3">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <ImageIcon size={36} weight="duotone" className="text-[#C4C0B5]" />
            )}
          </div>
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadingLogo}
            className="w-full mt-2 px-3 py-2 border border-[#1C1C1A] text-[#1C1C1A] rounded-lg text-[12px] font-semibold hover:bg-[#1C1C1A] hover:text-white transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="wizard-upload-logo"
          >
            <UploadSimple size={13} weight="bold" /> {uploadingLogo ? "…" : logoUrl ? "Cambia" : "Carica"}
          </button>
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onLogoSelected} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          type="button"
          onClick={onBack}
          className="bg-[#F1F4ED] text-[#1C1C1A] py-3 rounded-xl text-sm font-semibold hover:bg-[#E0E5D8] transition-colors"
          data-testid="wizard-back-2"
        >
          Indietro
        </button>
        <button
          type="button"
          onClick={onNext}
          className="bg-[#1C1C1A] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#7C8B6E] transition-colors flex items-center justify-center gap-2"
          data-testid="wizard-next-2"
        >
          Avanti <ArrowRight size={14} weight="bold" />
        </button>
      </div>
      <button
        type="button"
        onClick={onNext}
        className="block mt-2 w-full text-center text-[12px] text-[#9A968B] hover:text-[#1C1C1A] underline-offset-2 hover:underline"
        data-testid="wizard-skip-2"
      >
        Salta · li carico dopo
      </button>
    </div>
  );
}

/* ─────────── Step 3 · Contatti pubblici (email/indirizzo/sito) ─────────── */
function Step3Contacts({ agencyEmail, setAgencyEmail, agencyAddress, setAgencyAddress, agencyWebsite, setAgencyWebsite, onBack, onNext, busy }) {
  return (
    <div data-testid="wizard-step-3">
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A6A4D] inline-flex items-center gap-1.5">
        <EnvelopeSimple size={12} weight="fill" /> Step 3 · Contatti pubblici
      </div>
      <h4 className="text-[16px] font-semibold text-[#1C1C1A] mt-1.5">Dove ti trovano</h4>
      <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
        Compaiono sul fondo della tua pagina stima e su volantini e brochure. Tutti opzionali, ma più ne metti, più sembri &laquo;vero&raquo;.
      </p>

      <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-bold mt-4 block flex items-center gap-1">
        <EnvelopeSimple size={11} weight="fill" /> Email professionale
      </label>
      <input
        type="email"
        value={agencyEmail}
        onChange={(e) => setAgencyEmail(e.target.value)}
        placeholder="es. mario@casabella.it"
        className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
        data-testid="wizard-agency-email"
      />

      <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-bold mt-3 block flex items-center gap-1">
        <MapPin size={11} weight="fill" /> Indirizzo agenzia
      </label>
      <input
        type="text"
        value={agencyAddress}
        onChange={(e) => setAgencyAddress(e.target.value)}
        placeholder="es. Via Brera 12 · Milano"
        className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
        data-testid="wizard-agency-address"
      />

      <label className="text-[11px] uppercase tracking-[0.12em] text-[#6B6A65] font-bold mt-3 block flex items-center gap-1">
        <Globe size={11} weight="fill" /> Sito web (se ce l&apos;hai)
      </label>
      <input
        type="text"
        value={agencyWebsite}
        onChange={(e) => setAgencyWebsite(e.target.value)}
        placeholder="es. casabella.it"
        className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
        data-testid="wizard-agency-website"
      />

      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          type="button"
          onClick={onBack}
          className="bg-[#F1F4ED] text-[#1C1C1A] py-3 rounded-xl text-sm font-semibold hover:bg-[#E0E5D8] transition-colors"
          data-testid="wizard-back-3"
        >
          Indietro
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={busy}
          className="bg-[#1C1C1A] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#7C8B6E] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="wizard-next-3"
        >
          {busy ? "Salvo…" : <>Avanti <ArrowRight size={14} weight="bold" /></>}
        </button>
      </div>
      <button
        type="button"
        onClick={onNext}
        className="block mt-2 w-full text-center text-[12px] text-[#9A968B] hover:text-[#1C1C1A] underline-offset-2 hover:underline"
        data-testid="wizard-skip-3"
      >
        Salta · li metto dopo
      </button>
    </div>
  );
}

/* ─────────── Step 4 · Headline pagina stima ─────────── */
function Step4Headline({ value, setValue, onBack, onNext, busy }) {
  return (
    <div data-testid="wizard-step-4">
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A6A4D] inline-flex items-center gap-1.5">
        <Sparkle size={12} weight="fill" /> Step 4 · La tua voce
      </div>
      <h4 className="text-[16px] font-semibold text-[#1C1C1A] mt-1.5">La frase che apre la tua pagina stima</h4>
      <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
        È la prima cosa che legge un proprietario quando arriva sulla tua pagina pubblica. Una frase breve, personale, che dica chi sei o cosa fai diversamente.
      </p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="es. «Vendo case che amerei abitare. Poche, scelte con cura.»"
        rows={3}
        maxLength={180}
        className="w-full mt-3 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E] resize-none"
        data-testid="wizard-stima-headline"
      />
      <div className="text-[10px] text-[#9A968B] text-right mt-1">{value.length} / 180</div>

      <div className="bg-[#F1F4ED] border border-[#D4DCC8] rounded-lg p-3 mt-3">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#5A6A4D] mb-1">Esempi che funzionano</div>
        <ul className="text-[11.5px] text-[#3F4A33] space-y-1 italic">
          <li>«Conosco Brera come casa mia. Da 12 anni.»</li>
          <li>«Vendo case prima di pubblicarle online. Scrivimi.»</li>
          <li>«Trasparenza nella stima. Discrezione nella trattativa.»</li>
        </ul>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          type="button"
          onClick={onBack}
          className="bg-[#F1F4ED] text-[#1C1C1A] py-3 rounded-xl text-sm font-semibold hover:bg-[#E0E5D8] transition-colors"
          data-testid="wizard-back-4"
        >
          Indietro
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={busy}
          className="bg-[#1C1C1A] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#7C8B6E] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="wizard-next-4"
        >
          {busy ? "Salvo…" : <>Avanti <ArrowRight size={14} weight="bold" /></>}
        </button>
      </div>
      <button
        type="button"
        onClick={onNext}
        className="block mt-2 w-full text-center text-[12px] text-[#9A968B] hover:text-[#1C1C1A] underline-offset-2 hover:underline"
        data-testid="wizard-skip-4"
      >
        Salta · ci penso più tardi
      </button>
    </div>
  );
}

/* ─────────── Step 5 · Estensione Chrome ─────────── */
function Step5Extension({ onBack, onFinish }) {
  return (
    <div data-testid="wizard-step-5">
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#5A6A4D] inline-flex items-center gap-1.5">
        <Lightning size={12} weight="fill" /> Step 5 · L&apos;ultimo strumento
      </div>
      <h4 className="text-[16px] font-semibold text-[#1C1C1A] mt-1.5">Installa l&apos;estensione Chrome</h4>
      <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
        Pronto Publisher ti aiuta a compilare i form di
        <strong> Immobiliare.it</strong> e <strong>Idealista</strong>: i dati
        dell&apos;annuncio restano sempre a destra durante la pubblicazione, vendita o
        affitto. Installalo ora, lo trovi sempre nella barra di Chrome.
      </p>
      <a
        href={CHROME_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 w-full bg-[#1C1C1A] text-white py-3 rounded-xl text-sm font-semibold hover:bg-black transition-colors flex items-center justify-center gap-2"
        data-testid="wizard-install-extension"
      >
        <GoogleChromeLogo size={16} weight="fill" />
        Aggiungi a Chrome
        <ArrowSquareOut size={12} weight="bold" />
      </a>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button
          type="button"
          onClick={onBack}
          className="bg-[#F1F4ED] text-[#1C1C1A] py-3 rounded-xl text-sm font-semibold hover:bg-[#E0E5D8] transition-colors"
          data-testid="wizard-back-5"
        >
          Indietro
        </button>
        <button
          type="button"
          onClick={onFinish}
          className="bg-[#7C8B6E] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#6A7860] transition-colors flex items-center justify-center gap-2"
          data-testid="wizard-finish"
        >
          <CheckCircle size={14} weight="fill" /> Fatto · vai al tour
        </button>
      </div>
      <p className="text-[11px] text-[#9A968B] text-center mt-3 leading-snug">
        Puoi installarla anche più tardi da <em>Profilo → Estensione Chrome</em>.
        Adesso ti spiego in 5 schermate come funziona l&apos;app.
      </p>
    </div>
  );
}
